import { Diamond, Shield } from "components/icons/generated";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import TierInfo from "components/TierInfo";
import { BillingTierFeature, ScimOauthClient } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import ActionCard from "ds/components/ActionCard";

import { openSingleSignOnDrawer } from "../components/Drawer";
import SettingsSingleSignOnScimCredentials from "./Credentials";
import SettingsSingleSignOnScimDetails from "./Details";
import { showDeleteScimConfirmation } from "./Delete";
import { CREATE_SCIM_OAUTH_CLIENT, RESET_SCIM_OAUTH_CLIENT } from "./gql";

const useCreateScim = createDefaultMutationHook(CREATE_SCIM_OAUTH_CLIENT);
const useResetScim = createDefaultMutationHook(RESET_SCIM_OAUTH_CLIENT);

type SettingsSingleSignOnScimProps = {
  showUpgradeBanner: boolean;
  showUnsupportedBanner: boolean;
  settings:
    | Pick<ScimOauthClient, "clientID" | "accessTokenEndpoint" | "connnectorBaseURL">
    | undefined;
};

const SettingsSingleSignOnScim = ({
  settings,
  showUpgradeBanner,
  showUnsupportedBanner,
}: SettingsSingleSignOnScimProps) => {
  const isTierContainScim = useTierFeature(BillingTierFeature.Scim);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSSO,
    defaultCallbackTrackProperties: { method: "SCIM" },
  });

  const { reportSuccess } = useTypedContext(FlashContext);

  const [createScim, { loading: createLoading }] = useCreateScim({
    refetchQueries: ["GetSSOSettings"],
  });

  const [resetScim] = useResetScim({
    refetchQueries: ["GetSSOSettings"],
  });

  const handleSetup = () => {
    trackSegmentAnalyticsEvent("SCIM Set Up Click");
    createScim({}, (data) => {
      if (data?.createOauthClientForSCIM) {
        reportSuccess({ message: "SCIM credentials created" });
        openSingleSignOnDrawer({
          children: () => (
            <SettingsSingleSignOnScimCredentials credentials={data.createOauthClientForSCIM} />
          ),
          isDismissable: true,
        });
      }
    });
  };

  const handleShowDetails = () => {
    if (settings) {
      openSingleSignOnDrawer({
        children: () => <SettingsSingleSignOnScimDetails settings={settings} />,
        isDismissable: true,
      });
    }
    trackSegmentAnalyticsEvent("SCIM Details Click");
  };

  const handleReset = () => {
    trackSegmentAnalyticsEvent("SCIM Reset Click");
    resetScim({}, (data) => {
      if (data?.resetOauthClientForSCIM) {
        reportSuccess({ message: "SCIM credentials reset" });
        openSingleSignOnDrawer({
          children: () => (
            <SettingsSingleSignOnScimCredentials credentials={data.resetOauthClientForSCIM} />
          ),
          isDismissable: true,
        });
      }
    });
  };

  const handleRevoke = () => {
    showDeleteScimConfirmation();
    trackSegmentAnalyticsEvent("SCIM Revoke Click");
  };

  return (
    <ActionCard
      title="SCIM"
      description={
        <Box direction="column" gap="medium">
          Provision and manage users and groups from your identity provider using SCIM.
          {showUpgradeBanner && (
            <TierInfo
              variant="promo"
              type="banner"
              feature={BillingTierFeature.Scim}
              title="Upgrade account to use SCIM"
            >
              Automatically provision and manage users and groups from your identity provider by
              enabling SCIM.
            </TierInfo>
          )}
          {showUnsupportedBanner && (
            <TierInfo
              type="banner"
              variant="danger"
              feature={BillingTierFeature.Scim}
              title="SCIM is not supported by your plan"
            >
              You can still use or disable the integration, but to make changes, please upgrade to
              the <strong>Enterprise</strong> plan.
            </TierInfo>
          )}
        </Box>
      }
      icon={Shield}
      actionComponent={
        <Box gap="medium">
          {isTierContainScim && !settings && (
            <Button variant="secondary" onPress={handleSetup} loading={createLoading}>
              Set up
            </Button>
          )}

          {!isTierContainScim && !settings && (
            <Tooltip
              active
              on={(props) => (
                <Button variant="secondary" disabled {...props}>
                  Set up
                </Button>
              )}
            >
              Upgrade account to use SCIM
            </Tooltip>
          )}

          {settings && (
            <DropdownMenuEllipsis tooltip="SCIM actions" dotsSize="medium">
              <DropdownMenuItem onAction={handleShowDetails}>See details</DropdownMenuItem>
              <DropdownMenuItem
                isDisabled={!isTierContainScim}
                tooltip={
                  !isTierContainScim
                    ? "Upgrade account to be able to reset your credentials"
                    : undefined
                }
                onAction={handleReset}
              >
                Reset credentials
              </DropdownMenuItem>
              <DropdownMenuItem onAction={handleRevoke} danger>
                Revoke
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          )}
        </Box>
      }
      titleSuffix={
        <Box direction="row" gap="medium" align="center">
          {!isTierContainScim && (
            <Badge variant="purple" type="strong" startIcon={Diamond} iconOnly />
          )}
          {isTierContainScim && settings && <Badge variant="green" type="strong" text="Active" />}
        </Box>
      }
    />
  );
};

export default SettingsSingleSignOnScim;
